import { motion } from "framer-motion";
import { Zap, Check, Lock, ShieldCheck, Smartphone, RefreshCcw } from "lucide-react";

/* PricingSection — Título centralizado + imagem ao lado do card de preço */
export function PricingSection() {
  const primaryFeatures = [
    "8 módulos clínicos completos",
    "4 cartilhas para download em PDF",
    "4 calculadoras clínicas (MASCC, ECOG, EVA, Velocidade)",
    "26+ drogas classificadas com antídotos",
    "7 protocolos de emergência detalhados",
    "95+ questões de quiz clínico",
    "Acesso fácil em qualquer dispositivo",
    "Atualizações automáticas inclusas",
  ];
  const trustBadges = [
    { icon: ShieldCheck, label: "7 dias de garantia" },
    { icon: Smartphone,  label: "Acesso Online" },
    { icon: RefreshCcw,  label: "Atualizações inclusas" },
    { icon: Lock,        label: "Pagamento seguro" },
  ];

  return (
    <section id="pricing" className="py-16 sm:py-24 bg-card/30">
      <div className="container max-w-6xl mx-auto px-4">

        {/* ── Cabeçalho centralizado ── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="flex justify-center mb-4 sm:mb-5"
        >
          <div className="bg-destructive/10 border border-destructive/30 text-destructive text-xs sm:text-sm font-semibold px-4 sm:px-5 py-2 rounded-full inline-flex items-center gap-2">
            <Zap className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            Oferta especial de lançamento — Vagas limitadas
          </div>
        </motion.div>

        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-3"
        >
          Acesso completo à plataforma
        </motion.h2>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.15 }}
          className="text-center text-muted-foreground text-sm sm:text-base mb-8 sm:mb-12 max-w-xl mx-auto"
        >
          Tudo que você precisa para dominar o plantão oncológico, num único acesso vitalício.
        </motion.p>

        {/* ── Imagem acima do card ── */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="flex justify-center mb-6 sm:mb-8"
        >
          <img
            src="/images/pricing_platform_20260425.jpg"
            alt="Plataforma Jornada Oncológica — recursos disponíveis"
            className="w-full max-w-xs sm:max-w-2xl lg:max-w-5xl object-contain drop-shadow-2xl"
          />
        </motion.div>

        {/* ── Card de preço centralizado ── */}
        <div className="flex flex-col gap-8 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.25 }}
            className="w-full max-w-3xl mx-auto"
          >
            <div className="bg-card border-2 border-accent rounded-3xl p-6 sm:p-8 md:p-10 relative overflow-hidden">
              {/* Glow de fundo */}
              <div className="absolute inset-0 bg-gradient-to-br from-accent/10 via-transparent to-primary/5 pointer-events-none" />

              {/* Badge MAIS POPULAR */}
              <div className="absolute top-4 right-4 sm:top-5 sm:right-5 bg-accent text-accent-foreground text-xs font-bold px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full tracking-wide">
                ✦ MAIS POPULAR
              </div>

              <div className="relative z-10">
                {/* Preço com riscado */}
                <div className="mb-5 sm:mb-6">
                  <div className="flex items-baseline gap-2 sm:gap-3 flex-wrap">
                    <span className="text-muted-foreground line-through text-lg sm:text-xl font-semibold">
                      De R$ 97,00
                    </span>
                    <span className="bg-destructive/20 text-destructive text-xs font-bold px-2 py-0.5 rounded-full">
                      20% OFF
                    </span>
                  </div>
                  <div className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-accent mt-1 font-mono">
                    R$ 77,90
                  </div>
                  <div className="text-muted-foreground text-xs sm:text-sm mt-1">
                    Acesso vitalício · Pagamento único
                  </div>
                </div>

                {/* Lista de funcionalidades */}
                <ul className="space-y-2 sm:space-y-3 mb-6 sm:mb-8">
                  {primaryFeatures.map((feature, index) => (
                    <li key={index} className="flex items-start gap-2 sm:gap-3">
                      <Check className="w-4 h-4 sm:w-5 sm:h-5 text-accent shrink-0 mt-0.5" />
                      <span className="text-foreground text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA principal com pulse */}
                <motion.a
                  href="https://pay.kiwify.com.br/bGcYJms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block bg-accent text-accent-foreground w-full rounded-2xl py-4 sm:py-5 font-extrabold text-lg sm:text-xl text-center hover:bg-accent/90 transition-colors cursor-pointer"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  animate={{
                    boxShadow: [
                      "0px 0px 0px 0px rgba(104,211,145,0)",
                      "0px 0px 28px 6px rgba(104,211,145,0.35)",
                      "0px 0px 0px 0px rgba(104,211,145,0)",
                    ],
                  }}
                  transition={{ boxShadow: { repeat: Infinity, duration: 2.2 } }}
                >
                  QUERO ACESSO AGORA — R$ 77,90 →
                </motion.a>

                <p className="text-center text-xs text-muted-foreground mt-3">
                  🔒 Pagamento 100% seguro · Acesso imediato após confirmação
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* ── Selos de confiança ── */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 max-w-3xl mx-auto"
        >
          {trustBadges.map((badge, i) => {
            const Icon = badge.icon;
            return (
              <div key={i} className="flex flex-col items-center gap-2 bg-card border border-border rounded-2xl p-3 sm:p-4 text-center">
                <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-accent" />
                <span className="text-xs text-muted-foreground font-medium">{badge.label}</span>
              </div>
            );
          })}
        </motion.div>

      </div>
    </section>
  );
}
